import styled from '@emotion/styled';
import { Grid, Typography } from '@material-ui/core';
import ArrowForward from '@material-ui/icons/ArrowForward';
import Save from '@material-ui/icons/Save';
import Undo from '@material-ui/icons/Undo';
import { createElement, Fragment, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';

import { Option } from '../../../../generated/graphql';
import { useGetIsPC } from '../../../../utilityHooks/useGetIsPC';
import InputtedProjectPointTotal from '../../../component/compound/InputtedProjectPointTotal';
import OptionMerchandiseList from '../../../component/compound/OptionMerchandiseForm/OptionMerchandiseList';
import Spinner from '../../../component/compound/Spinner';
import ButtonWithIcon from '../../../component/primitive/ButtonWithIcon';
import { SCREEN_LARGE } from '../../../constant/pageWidth';
import { SPACING_HUGE, SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { context as DependencyContext } from '../../../context/dependency';
import { context as StateContext } from '../../../context/state';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import mq from '../../../styleUtility/mediaquery';
import { editingProjectStateAtom } from '../atoms/editingProjectStateAtom';
import { projectLoadingAtom } from '../atoms/projectAtom';
import { isPointRequired as getIsPointRequired } from '../domain/helperFunctions';
import useUpdateProject from '../hooks/useUpdateProject';
import LeftWorkflowStepper from './LeftWorkflowStepper';
import MobileOnlyWorkflowStepper from './MobileOnlyWorkflowStepper';

const OptionScreen = () => {
  const isPC = useGetIsPC();
  const isLoading = useRecoilValue(projectLoadingAtom);
  const state = useRecoilValue(editingProjectStateAtom);
  const { updateDraftProjectAndGoToDifferentTab, updateDraftProjectAndGoToProjectsList } = useUpdateProject();
  const {
    editProjectUsecase: { updateSelectedProductOption: updateSelectedProductOption },
  } = useContext(DependencyContext);
  const {
    acct,
    pricingMerchandiseOptions: { availablePricingMerchandiseOptions, selectedPricingMerchandiseOptions },
    lastAccessedOrganization,
  } = useContext(StateContext);

  const productPricing = acct.productPricing || undefined;
  const isPointRequired = getIsPointRequired(lastAccessedOrganization);

  if (isLoading || !state) return <Spinner fullHeight />;

  return (
    <Root>
      <Grid container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={isPC ? 4 : 2}>
        <LeftWorkflowStepper
          step="option"
          updateDraftAndGoToTargetStep={(targetStep) =>
            updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'option', targetStep })
          }
        />
        <Grid item xs={12} sm={12} md={9} lg={9}>
          <OptionContainer>
            {isLoading ? (
              <Spinner fullHeight />
            ) : (
              <>
                {/* Inputted Project Total Point */}
                <InputtedProjectPointTotal step="option" />

                {/* Options */}
                <Heading variant="h5">
                  <FormattedMessage id="project_editing_page.option_setting.title" />
                </Heading>
                <OptionMerchandiseList
                  isPointRequired={isPointRequired}
                  productPricing={productPricing}
                  creationSet={state.creationSet || undefined}
                  pricingMerchandiseOptions={
                    (availablePricingMerchandiseOptions.map((option) =>
                      option.toJSON(),
                    ) as unknown) as ReadonlyArray<Option>
                  }
                  selectedPricingMerchandiseOptionIds={selectedPricingMerchandiseOptions}
                  onChangeSwitch={updateSelectedProductOption}
                />
              </>
            )}
          </OptionContainer>
        </Grid>
      </Grid>

      <MobileOnlyWorkflowStepper
        step="option"
        updateDraftAndGoToTargetStep={(targetStep) =>
          updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'option', targetStep })
        }
      />

      <Actions>
        <Buttons>
          <ButtonWithIcon
            icon={Undo}
            color="default"
            variant="contained"
            onClick={() => updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'option', targetStep: 'brief' })}
          >
            <FormattedMessage id="project_editing_page.option_setting.buttons.back" />
          </ButtonWithIcon>
          <ButtonWithIcon
            icon={Save}
            color="default"
            variant="contained"
            onClick={() => updateDraftProjectAndGoToProjectsList({ state, currentStep: 'option' })}
          >
            <FormattedMessage id="project_editing_page.option_setting.buttons.save_as_draft" />
          </ButtonWithIcon>
          <ButtonWithIcon
            icon={ArrowForward}
            variant="contained"
            color="primary"
            onClick={() =>
              updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'option', targetStep: 'preview' })
            }
          >
            <FormattedMessage id="project_editing_page.option_setting.buttons.submit" />
          </ButtonWithIcon>
        </Buttons>
      </Actions>
    </Root>
  );
};

const Root = styled.section`
  width: auto;
  max-width: ${SCREEN_LARGE}px;
  margin-top: ${SPACING_HUGE}px;
  margin-right: auto;
  margin-left: auto;
  padding-bottom: ${SPACING_UNIT * 12}px;

  ${mq('sp')} {
    margin-top: ${SPACING_REGULAR}px;
    padding-left: ${SPACING_REGULAR}px;
    padding-right: ${SPACING_REGULAR}px;
    padding-bottom: ${SPACING_UNIT * 6}px;
  }
`;

const Heading = styled(Typography)`
  margin-bottom: ${SPACING_LARGE}px;

  ${mq('sp')} {
    font-size: 1.3rem;
  }
`;

const RequiredPoint = styled(Typography)`
  ${flexibleLayoutContainer({
    alignItems: 'center',
  })}
  font-weight: 700;
`;

RequiredPoint.defaultProps = { variant: 'body1', color: 'primary' };

const Actions = styled.div`
  ${flexibleLayoutContainer({
    justifyContent: 'flex-end',
  })}
  margin-top: ${SPACING_LARGE}px;
`;

const Buttons = styled.div`
  > button {
    margin-left: ${SPACING_UNIT}px;
  }

  ${mq('sp')} {
    width: 100%;
    display: flex;
    flex-direction: column-reverse;
    margin-top: ${SPACING_REGULAR}px;

    > button {
      margin-bottom: ${SPACING_UNIT}px;
      margin-left: 0;
    }
  }
`;

const OptionContainer = styled.div`
  margin-bottom: ${SPACING_LARGE}px;
`;

export default OptionScreen;
